import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { LogOut, Settings, Bell, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { SyncBanner } from "@/components/SyncBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useRef, useEffect } from "react";
import { subscribeToPush } from "@/lib/pushNotifications";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AppLayoutProps {
  children: React.ReactNode;
}

const severityIcon = (s: string) => {
  if (s === "alta" || s === "critica") return <AlertCircle className="h-4 w-4 text-destructive shrink-0" />;
  if (s === "media") return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  return <Info className="h-4 w-4 text-muted-foreground shrink-0" />;
};

export function AppLayout({ children }: AppLayoutProps) {
  const { profile, user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Push notifications: only subscribe if user already granted permission (opt-in)
  useEffect(() => {
    if (user?.id && typeof Notification !== "undefined" && Notification.permission === "granted") {
      subscribeToPush(user.id).catch((err) => console.warn("[Push] subscribe failed (non-critical):", err));
    }
  }, [user?.id]);

  const { data: alerts = [] } = useQuery({
    queryKey: ["active-alerts"],
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alerts")
        .select("id, title, description, severity, created_at")
        .eq("resolved", false)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) return [];
      return data || [];
    },
  });

  // Realtime subscription for alerts instead of polling
  useEffect(() => {
    const channel = supabase
      .channel("alerts-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "alerts" }, () => {
        queryClient.invalidateQueries({ queryKey: ["active-alerts"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  const alertCount = alerts.length;

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "U";

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between border-b bg-card px-4 shrink-0">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="min-h-[44px] min-w-[44px]" />
            </div>
            <div className="flex items-center gap-1">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative min-h-[44px] min-w-[44px]">
                    <Bell className="h-4 w-4" />
                    {alertCount > 0 && (
                      <span className="absolute top-1.5 right-1.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                        {alertCount > 9 ? "9+" : alertCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0">
                  <div className="p-3 border-b">
                    <h4 className="font-semibold text-sm">Alertas</h4>
                  </div>
                  <ScrollArea className="max-h-72">
                    {alerts.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">Nenhum alerta pendente</p>
                    ) : (
                      <div className="divide-y">
                        {alerts.map((a) => (
                          <button
                            key={a.id}
                            className="w-full text-left px-3 py-2.5 hover:bg-accent transition-colors flex gap-2 items-start"
                            onClick={() => navigate("/")}
                          >
                            {severityIcon(a.severity)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{a.title}</p>
                              {a.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">{a.description}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 min-h-[44px]">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">
                      {profile?.full_name || user?.email || "Usuário"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-sm font-medium">{profile?.full_name || "Usuário"}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/configuracoes")} className="min-h-[44px]">
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    if (window.confirm("Tem certeza que deseja sair?")) signOut();
                  }} className="min-h-[44px]">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <SyncBanner />
          <main ref={mainRef} className="flex-1 overflow-auto p-4 md:p-6 pb-20 md:pb-6">
            {children}
          </main>
        </div>
      </div>
      <MobileBottomNav />
    </SidebarProvider>
  );
}
